import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { lastValueFrom } from "rxjs";

import { setFavicon } from "../services/core/CommonService";
import { showNotification1 } from "../services/notifyService";
import { getUserSession, setUserSession, updateToken } from "../services/sessionService";
import { get, post } from "../services/smartApiService";


interface modalOptionsProps {
  title?: ReactNode | string | undefined;
  content: ReactNode | string | undefined;
  width?: number,
  className?: string;
  closeBody?: boolean;
  closeFunction?: () => void;
}
// Define the context shape
interface SiteContextProps {
  user: any,
  setUser: (data: any | null) => void;
  openModal: (options: modalOptionsProps) => void;
  closeModal: (action?: "all" | "wait" | "again") => void;
  reOpenModal: () => void;
  isModalOpen: boolean;
  modalOptions: modalOptionsProps[];
  logout: () => void;
  handleUserChange: (name: string, value: any) => void;
  hasTriggered: boolean;
  setHasTriggered: (value: boolean) => void;
  title: string;
  setTitle: (title: string) => void;
  setLoading: (status: boolean, msg?: string) => void;
  setRight: (content: ReactNode) => void;
  rightContent: ReactNode,
  sideNav: any[],
  setSideNav: Dispatch<SetStateAction<any[]>>
  setIsVisible: (value: boolean) => void;
  isVisible: boolean;  
  siteSettings: any,
  setSiteSettings: Dispatch<SetStateAction<any>>
}

// Create the context with the default value of `undefined`
const SiteContext = createContext<SiteContextProps | undefined>(undefined);

// Custom hook to use the SiteContext
export const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error("useSiteContext must be used within a SiteProvider");
  }
  return context;
};

// Define the props for SiteProvider
interface SiteProviderProps {
  children: ReactNode;
}

export const SiteProvider: React.FC<SiteProviderProps> = ({ children }) => {
  const [user, setUserData] = useState<any | null>(getUserSession(""));
  // const [modalOptions, setModalOptions] = useState<any>(null);
  const [modalOptions, setModalOptions] = useState<modalOptionsProps[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [title, setTitle] = useState(" HD Tech Solutions ");
  const [rightContent, setRight] = useState<ReactNode>(null);
  const [sideNav, setSideNav] = useState<any[]>([]);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [subcriptonData, setSubcriptonData] = useState<any>({});
  const [siteSettings, setSiteSettings] = useState<any>({});

  const refreshTimeoutRef = useRef<NodeJS.Timeout>();

 

 




  

  // Handle user state changes
  const handleUserChange = (name: string, value: any) => {
    setUserData((prev: any) => ({ ...prev, [name]: value }));
  };

  // Set user and persist session data
  const setUser = (data: any | null) => {
    setUserData(data);
    setUserSession(data);
  };


  // add modal object to open modal
  const openModal = useCallback((options: any) => {
    setModalOptions((prev: any[]) => [...prev, options]);
    setIsModalOpen(true);
  }, []);


  // close the modal
  const closeModal = useCallback((action: "all" | "wait" | "again" | "" = "") => {
    setModalOptions((prevModals) => {
      if (prevModals.length < 1) return prevModals;
  
      // For "wait" action: add 'dis-hide' class to all modals
      if (action === "wait") {
        return prevModals.map((modal) => ({
          ...modal,
          className: `${modal?.className ?? ""} dis-hide`.trim(),
        }));
      }
  
      // For "all" action: clear all modals and close modal system
      if (action === "all") {
        return [];
      }
  
      // For closing last modal or 'again' action
      if (action === "again") {
        return prevModals.slice(0, -1);
      }
      
      // Default: close last modal, call closeFunction if exists
      const lastModal = prevModals[prevModals.length - 1];
      if (lastModal?.closeFunction) {
        // wait for stack exicution complete and then exicute closeFunction
        setTimeout(() => {
          lastModal?.closeFunction?.();
        }, 0);
        return prevModals;
      } else {
        return prevModals.slice(0, -1);
      }
    });
  }, []);

 
  // Reopen the closed modals using remove the hide class form the each modal object
  const reOpenModal = () => {
    if (isModalOpen && modalOptions && modalOptions.length > 0) {
      // console.log("Modal reopened")
      setModalOptions(prev => prev.map((p) => ({
        ...p, className: p?.className?.replace("dis-hide", "").trim() ?? ""
      })));
    }
  }


  // Update user session on load
  const updateUserOnLoad = () => {
    const session_data = getUserSession("");
    if (session_data) {
      setUserData(session_data);
      if (session_data.ename !== undefined) {
        // Additional logic for `ename` if needed
      }
    }
  };

  // Logout user and show alert
  const logout = () => {
    try {
      get("auth/logout").subscribe((response) => {
        // showAlertAutoClose("Log Out Successful", "success");
        showNotification1("success", "Log Out Successful");
        closeModal("all")
        setUser(null);
      })
    } catch (error) {
      closeModal("all")
      setUser(null);
    }
  };

  //Auto-refresh token setup
  const setupTokenRefresh = useCallback((token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresIn = (payload.exp * 1000) - Date.now();
      const refreshTime = Math.max(expiresIn - 2 * 60 * 1000, 1000); // Refresh 2 mins before expiry
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      console.log("setup refresh token refresh " , refreshTime);
      refreshTimeoutRef.current = setTimeout(async () => {
        console.log("time check is enabled ")
        await refreshToken();
      }, refreshTime);
      // console.log(`Token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`);
    } catch (error) {
      // console.error('Error setting up token refresh:', error);
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshTokenStr = getUserSession("refresh_token");
      if (!refreshTokenStr) {
        console.warn("No refresh token available");
        setUser(null);
        return false;
      }
      const response = await lastValueFrom(
        post("auth/refresh", { refresh_token: refreshTokenStr }, { showLoading: false })
      );
      if (response && response.data && response.data.access_token) {
        updateToken(response.data.access_token);
        setupTokenRefresh(response.data.access_token);
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      setUser(null);
      return false;
    }
  }, []);

  // useEffect(() => {
  //   const initializeAuth = async () => {
  //     if (user) {
  //       // Try to refresh token to validate it
  //       const isValid = await refreshToken();
  //       if (isValid) {
  //         // Decode token to get user info
  //         try {
  //           const payload = JSON.parse(atob(user.access_token.split('.')[1]));
  //           setUser({ id: payload.userId, email: payload.email });
  //           setupTokenRefresh(user.access_token);
  //         } catch (error) {
  //         //  console.error('Error decoding token:', error);
  //           //localStorage.removeItem('access_token');
  //         }
  //       }
  //     }
  //     // setIsLoading(false);
  //   };

  //   initializeAuth();
  // }, [refreshToken, setupTokenRefresh]);


  // // Handle page visibility change (refresh when tab becomes active)
  useEffect(() => {
    if(user){
      //  console.log("entrrting in refresh token timer");
       setupTokenRefresh(user.access_token);
    }
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // console.log("started refresh token ");
       // refreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const setLoading = (status: boolean, msg?: string) => {

  }




  // Effect to update user on component mount
  useEffect(() => {
    // updateUserOnLoad();
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return (
    <SiteContext.Provider
      value={{
        user,
        setUser,
        openModal,
        closeModal,
        isModalOpen,
        modalOptions,
        logout,
        handleUserChange,
        hasTriggered,
        setHasTriggered,
        title,
        setTitle,
        setLoading,
        setRight,
        rightContent,
        reOpenModal,
        sideNav,
        setSideNav,
        setIsVisible,
        isVisible,    
        siteSettings, setSiteSettings
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};